import { makeStyles, alpha } from '@material-ui/core/styles'
import clsx from 'clsx'
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  TextField,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip,
} from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import BookmarkIcon from '@material-ui/icons/Bookmark'
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import { useNavigate, useLocation } from 'react-router-dom'
import { useState } from 'react'

import useCurrentChatId from '@/components/hooks/use-current-chat-id'
import useConversationsStore from '@/stores/useConversationsStore'

import { useAwsCredentialsManager } from '../../context/AWS-credentials.context'
import { useAuth0 } from '@/auth/index'
import usePinConversation from '../../hooks/usePinConversation'
import useRenameConversation from '../../hooks/useRenameConversation'
import ConfirmationModal from './confirmation-modal'
import { getConfig } from '@/config/runtime'

const useStyles = makeStyles((theme) => {
  const fadeAreaWidth = theme.spacing(20)
  const buttonRightOffset = theme.spacing(2)

  return {
    cardRoot: {
      borderRadius: theme.spacing(2),
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
      border: `1px solid ${theme.palette.grey[300]}`,
      backgroundColor: 'transparent',
      cursor: 'pointer',
      marginBottom: theme.spacing(2),
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(1),
      transition: 'all 0.2s ease-in-out',
      position: 'relative',
      '&:hover': {
        backgroundColor: alpha(theme.palette.action.hover, 0.03),
        borderColor: theme.palette.primary.main,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
        '& $title': {
          maskImage: `linear-gradient(to right, black calc(100% - ${fadeAreaWidth}px), transparent 100%)`,
          WebkitMaskImage: `linear-gradient(to right, black calc(100% - ${fadeAreaWidth}px), transparent 100%)`,
        },
        '& $actionButtons': {
          visibility: 'visible',
          opacity: 1,
        },
      },
    },
    cardContent: {
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      padding: `${theme.spacing(2)}px ${theme.spacing(2.5)}px`,
      '&:last-child': {
        paddingBottom: theme.spacing(2),
      },
    },
    title: {
      flexGrow: 1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minWidth: 0,
      margin: 0,
      transition: 'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
      fontSize: '1.25rem',
    },
    selected: {
      border: `2px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.action.hover,
    },
    actionButtons: {
      position: 'absolute',
      right: buttonRightOffset,
      top: '50%',
      transform: 'translateY(-50%)',
      visibility: 'hidden',
      opacity: 0,
      transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
      display: 'flex',
      gap: theme.spacing(0.5),
      zIndex: 1,
    },
    actionButton: {
      color: theme.palette.grey[600],
      padding: theme.spacing(0.75),
      borderRadius: theme.shape.borderRadius,
      transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: alpha(theme.palette.grey[900], 0.03),
        color: theme.palette.grey[900],
      },
      '&.Mui-disabled': {
        color: theme.palette.action.disabled,
        backgroundColor: 'transparent',
      },
    },
    editingButtons: {
      position: 'absolute',
      right: buttonRightOffset,
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      gap: theme.spacing(0.5),
      zIndex: 1,
    },
    editTextField: {
      flexGrow: 1,
      '& .MuiInputBase-root': {
        fontSize: '1.25rem',
      },
      '& .MuiInputBase-input': {
        padding: theme.spacing(0.5, 0),
      },
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    pinIndicator: {
      color: theme.palette.primary.main,
      marginRight: theme.spacing(1),
      fontSize: '0.875rem',
    },
    overflowButton: {
      color: theme.palette.grey[600],
      padding: theme.spacing(0.75),
      borderRadius: theme.shape.borderRadius,
      transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: alpha(theme.palette.grey[900], 0.03),
        color: theme.palette.grey[900],
      },
    },
  }
})

interface ChatItemProps {
  chatId: string
  chatName: string
}

const ChatItem = ({ chatId, chatName }: ChatItemProps) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const location = useLocation()

  const [isDeleting, setIsDeleting] = useState(false)
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(
    null
  )
  const { applicationId, credentials } = useAwsCredentialsManager()
  const { user } = useAuth0()

  const currentChatId = useCurrentChatId()
  const isChatSelected = currentChatId === chatId

  const removeConversation = useConversationsStore((state) => state.removeConversation)

  const {
    isEditing,
    isLoading,
    editedName,
    setEditedName,
    startEditing,
    cancelEditing,
    submitRename,
  } = useRenameConversation({ conversationId: chatId, initialName: chatName })

  const {
    isLoading: isPinLoading,
    isPinned,
    togglePin,
  } = usePinConversation({ conversationId: chatId, conversationTitle: chatName })

  const handleNavigateToChat = () => {
    if (isDeleting || isEditing) return
    const basePath = location.pathname.split('/plus')[0]
    navigate(`${basePath}/plus/${chatId}`)
  }

  const handleSaveClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    await submitRename()
  }

  const handleCancelClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    cancelEditing()
  }

  const handleTextFieldKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      submitRename()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      cancelEditing()
    }
  }

  const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditedName(event.target.value)
  }

  const handleTextFieldClick = (event: React.MouseEvent) => {
    event.stopPropagation()
  }


  const handleCloseConfirmModal = () => {
    setOpenConfirmModal(false)
  }

  // Menu handlers
  const handleOverflowClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setContextMenuPosition(null)
  }

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setContextMenuPosition({ x: event.clientX, y: event.clientY })
  }

  // Pin/unpin handler
  const handleTogglePin = async () => {
    handleMenuClose()
    try {
      await togglePin()
    } catch (error) {
      console.error('Pin/unpin failed:', error)
      // Error is handled by the hook and stored in pinError state
    }
  }

  const handleMenuEdit = () => {
    handleMenuClose()
    startEditing()
  }

  const handleMenuDelete = () => {
    handleMenuClose()
    setOpenConfirmModal(true)
  }

  const confirmDeleteChat = async () => {
    handleCloseConfirmModal()
    if (isDeleting) return

    setIsDeleting(true)

    const API_ENDPOINT = `${getConfig().plus.apiUrl}/conversations/`

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'DELETE',
        body: JSON.stringify({
          conversationId: chatId,
          email: user.email,
          applicationId,
          accessKeyId: credentials?.AccessKeyId,
          secretAccessKey: credentials?.SecretAccessKey,
          sessionToken: credentials?.SessionToken,
        }),
      })

      if (response.ok) {
        const responseData = await response.json()

        if (responseData.$metadata && responseData.$metadata.httpStatusCode === 200) {
          removeConversation(chatId)
          if (currentChatId === chatId) {
            const basePath = location.pathname.split('/plus')[0]
            navigate(`${basePath}/plus`)
          }
        } else {
          console.error(
            'API response structure not as expected or status code mismatch:',
            responseData
          )
        }
      } else {
        console.error('API error:', response.status, await response.text())
      }
    } catch (error) {
      console.error('Network or other error during delete:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <Card
        className={clsx(classes.cardRoot, { [classes.selected]: isChatSelected })}
        variant="outlined"
        onClick={handleNavigateToChat}
        onContextMenu={handleContextMenu}
      >
        <CardContent className={classes.cardContent}>
          {isEditing ? (
            <>
              <div className={classes.loadingContainer}>
                <TextField
                  className={classes.editTextField}
                  value={editedName}
                  onChange={handleTextFieldChange}
                  onKeyDown={handleTextFieldKeyDown}
                  onClick={handleTextFieldClick}
                  disabled={isLoading}
                  variant="standard"
                  autoFocus
                  InputProps={{
                    disableUnderline: false,
                  }}
                />
                {isLoading && <CircularProgress size={16} />}
              </div>
              <div className={classes.editingButtons}>
                <IconButton
                  aria-label="save changes"
                  className={classes.actionButton}
                  onClick={handleSaveClick}
                  disabled={isLoading || !editedName.trim()}
                  size="small"
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="cancel editing"
                  className={classes.actionButton}
                  onClick={handleCancelClick}
                  disabled={isLoading}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
                {isPinned && (
                  <Tooltip title="This conversation is pinned">
                    <BookmarkIcon className={classes.pinIndicator} fontSize="small" />
                  </Tooltip>
                )}
                <Typography className={classes.title} title={chatName}>
                  {chatName}
                </Typography>
              </div>
              <div className={classes.actionButtons}>
                <IconButton
                  aria-label="conversation actions"
                  className={classes.overflowButton}
                  onClick={handleOverflowClick}
                  disabled={isDeleting || isPinLoading}
                  size="small"
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmationModal
        open={openConfirmModal}
        onClose={handleCloseConfirmModal}
        onConfirm={confirmDeleteChat}
        title="Confirm Deletion"
        message={`Are you sure you want to delete the conversation "${chatName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
        loadingText="Deleting..."
      />

      {/* Overflow Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
        }}
      >
        <MenuItem onClick={handleTogglePin} disabled={isPinLoading}>
          {isPinned ? (
            <>
              <BookmarkBorderIcon fontSize="small" style={{ marginRight: 8 }} />
              Unpin
            </>
          ) : (
            <>
              <BookmarkIcon fontSize="small" style={{ marginRight: 8 }} />
              Pin
            </>
          )}
        </MenuItem>
        <MenuItem onClick={handleMenuEdit} disabled={isPinLoading}>
          <EditIcon fontSize="small" style={{ marginRight: 8 }} />
          Rename
        </MenuItem>
        <MenuItem onClick={handleMenuDelete} disabled={isPinLoading}>
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Context Menu */}
      <Menu
        open={Boolean(contextMenuPosition)}
        onClose={handleMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenuPosition
            ? { top: contextMenuPosition.y, left: contextMenuPosition.x }
            : undefined
        }
        PaperProps={{
          elevation: 3,
        }}
      >
        <MenuItem onClick={handleTogglePin} disabled={isPinLoading}>
          {isPinned ? (
            <>
              <BookmarkBorderIcon fontSize="small" style={{ marginRight: 8 }} />
              Unpin
            </>
          ) : (
            <>
              <BookmarkIcon fontSize="small" style={{ marginRight: 8 }} />
              Pin
            </>
          )}
        </MenuItem>
        <MenuItem onClick={handleMenuEdit} disabled={isPinLoading}>
          <EditIcon fontSize="small" style={{ marginRight: 8 }} />
          Rename
        </MenuItem>
        <MenuItem onClick={handleMenuDelete} disabled={isPinLoading}>
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  )
}

export default ChatItem
